import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LOCALE_NATIVE_NAMES, SUPPORTED_LOCALES, type Locale } from '@wms/shared';
import { changeLocale } from '../../lib/i18n';
import { apiFetch, ApiError } from '../../lib/api';
import { useInvalidateSession } from '../../lib/session';

/** 登录页（UX S1）。不区分"账号不存在"与"密码错误"——两者都显示同一条错误。 */
export function LoginPage(): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const invalidateSession = useInvalidateSession();

  // 【U-06】登录凭据 = 公司代码 + 用户名 + 密码。公司代码记在本机，换人登录时
  // 不用每次重敲（它不是秘密，只是定位公司的标识）。
  const [companyCode, setCompanyCode] = useState(
    () => localStorage.getItem('wms.lastCompanyCode') ?? '',
  );
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<{ code: string; params: Record<string, unknown> } | null>(
    null,
  );

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result = await apiFetch<{ mustChangePassword: boolean }>('/auth/login', {
        method: 'POST',
        body: { companyCode, username, password, rememberDevice },
      });
      localStorage.setItem('wms.lastCompanyCode', companyCode);
      await invalidateSession();
      navigate(result.mustChangePassword ? '/change-password' : '/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) setError({ code: err.code, params: err.params });
      else setError({ code: 'COMMON.INTERNAL_ERROR', params: {} });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-page__card card">
        <h1 className="auth-page__brand">{t('app.name')}</h1>
        <form onSubmit={(e) => void submit(e)}>
          <label className="field">
            <span>{t('auth.companyCode')}</span>
            <input
              type="text"
              autoComplete="organization"
              autoFocus={companyCode === ''}
              value={companyCode}
              onChange={(e) => setCompanyCode(e.target.value)}
              required
            />
          </label>
          <label className="field">
            <span>{t('auth.username')}</span>
            <input
              type="text"
              autoComplete="username"
              autoFocus={companyCode !== ''}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </label>
          <label className="field">
            <span>{t('auth.password')}</span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </label>
          <label className="switch" style={{ margin: 'var(--space-2) 0 var(--space-4)' }}>
            <input
              type="checkbox"
              checked={rememberDevice}
              onChange={(e) => setRememberDevice(e.target.checked)}
            />
            <span className="switch__track" />
            <span>{t('auth.rememberDevice')}</span>
          </label>

          {error && (
            <p className="field-error">
              {t(`errors:${error.code}`, error.params as Record<string, unknown>)}
            </p>
          )}

          <button
            type="submit"
            className="btn btn--primary"
            style={{ width: '100%' }}
            disabled={submitting}
          >
            {t('auth.submit')}
          </button>
        </form>

        <nav className="auth-page__locales" aria-label={t('shell.language')}>
          {SUPPORTED_LOCALES.map((locale: Locale) => (
            <button
              key={locale}
              type="button"
              className="shell__locale"
              aria-current={i18n.language === locale}
              onClick={() => void changeLocale(locale)}
            >
              {LOCALE_NATIVE_NAMES[locale]}
            </button>
          ))}
        </nav>
      </div>
    </div>
  );
}
